import type Konva from 'konva';
import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { PAD_B, PAD_L, PAD_R, PAD_T, PRINT_STAGE_W } from './constants';
import type { ChartDimensions } from './types';

/**
 * Hook to calculate the width, height, and scale of the Konva Stage
 * based on the window size. It uses a ref to get the container dimensions
 * and returns the stage size and scale.
 *
 * @param width - The base width of the stage.
 * @param height - The base height of the stage.
 *
 * @returns An object containing a ref for the container div,
 *          the responsive width and height of the stage,
 *          and the scale factor.
 *
 * @see https://konvajs.org/docs/sandbox/Responsive_Canvas.html
 */
export function useResponsiveStage(width: number, height: number) {
    const stageWidth = width;
    // FIXME this height is not used as height of container is not changed usually.
    const stageHeight = height;

    const [stageSize, setStageSize] = useState({
        width: stageWidth,
        height: stageHeight,
        scale: 1,
    });

    const containerRef = useRef<HTMLDivElement>(null);
    // `<Stage ref>` hands back the Konva stage itself, which is what exposes `draw`.
    const stageRef = useRef<Konva.Stage>(null);

    const updateSize = () => {
        if (!containerRef.current) return;

        const containerWidth = containerRef.current.offsetWidth;
        const scale = containerWidth / stageWidth;

        setStageSize({
            width: stageWidth * scale,
            height: stageHeight * scale,
            scale: scale,
        });
    };

    /*
     * Konva repaints synchronously only when the stage's own size changes; the
     * shapes that React just re-rendered are queued for the next animation
     * frame, which the print snapshot never waits for. So the size is committed
     * synchronously and the stage is drawn by hand.
     */
    const drawSynchronously = (size: { width: number; height: number; scale: number }) => {
        flushSync(() => {
            setStageSize(size);
        });
        stageRef.current?.draw();
    };

    const onPrintStart = () => {
        const scale = PRINT_STAGE_W / stageWidth;

        drawSynchronously({
            width: PRINT_STAGE_W,
            height: stageHeight * scale,
            scale: scale,
        });
    };

    const onPrintEnd = () => {
        if (!containerRef.current) return;

        const scale = containerRef.current.offsetWidth / stageWidth;

        drawSynchronously({
            width: stageWidth * scale,
            height: stageHeight * scale,
            scale: scale,
        });
    };

    // Update on mount and when window resizes
    // biome-ignore lint/correctness/useExhaustiveDependencies: Only register once
    useEffect(() => {
        updateSize();
        window.addEventListener('resize', updateSize);

        return () => {
            window.removeEventListener('resize', updateSize);
        };
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Only register once
    useEffect(() => {
        window.addEventListener('beforeprint', onPrintStart);
        window.addEventListener('afterprint', onPrintEnd);
        return () => {
            window.removeEventListener('beforeprint', onPrintStart);
            window.removeEventListener('afterprint', onPrintEnd);
        };
    }, []);

    return {
        containerRef,
        stageRef,
        width: stageSize.width,
        height: stageSize.height,
        scale: stageSize.scale,
    };
}

/**
 * Hook to calculate the chart dimensions based on the stage size.
 *
 * @param stageWidth - The width of the stage.
 * @param stageHeight - The height of the stage
 *
 * @returns An object containing the chart dimensions.
 */
export function useChartDimensions(stageWidth: number, stageHeight: number): ChartDimensions {
    const chartLeft = PAD_L;
    const chartRight = stageWidth - PAD_R;
    const chartTop = PAD_T;
    const chartBottom = stageHeight - PAD_B;
    const chartWidth = chartRight - chartLeft;
    const chartHeight = chartBottom - chartTop;
    const xOrigin = chartLeft + chartWidth / 2;
    const yOrigin = chartTop + chartHeight / 2;

    return {
        left: chartLeft,
        top: chartTop,
        right: chartRight,
        bottom: chartBottom,
        width: chartWidth,
        height: chartHeight,
        xOrigin: xOrigin,
        yOrigin: yOrigin,
    };
}

/**
 * Hook to get the range of values, symmetric around zero and rounded up to the nearest multiple of `increment`.
 *
 * @param values The values to get the range of.
 * @param increment The increment to round up to. Defaults to 1.
 *
 * @returns An object containing the min, max, and range of the values.
 */
export function useRange(values: number[], increment = 1): { min: number; max: number; range: number } {
    return useMemo(() => {
        if (values.length === 0) {
            return { min: -increment, max: increment, range: increment * 2 };
        }
        const min = Math.min(...values);
        const max = Math.max(...values);
        const absMax = Math.max(Math.abs(min), Math.abs(max), increment);
        const value = Math.ceil(absMax / increment) * increment;
        return { min: -value, max: value, range: value * 2 };
    }, [values, increment]);
}
